/* eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Box, Paper, Typography, CircularProgress, Checkbox, Button } from '@mui/material';
import { List, useDynamicRowHeight, useListRef, type RowComponentProps } from 'react-window';
import ActionMenu from './action-menu';
import ProvisionSelectorPopup from './popup/provision-selector-popup';
import { DropdownOption } from './ui/dropdown';
import TablePlaceholder from './table-placeholder';
import { Link, RefreshCw, CalendarClock, ClipboardPaste } from 'lucide-react';
import { useSettings } from '../providers/settings-provider';
import { usePermissions } from '../providers/permissions-provider';
import { reformatGstDescriptionDates } from '../util/date';

export type FilterParams = {
    sortBy: 'description' | 'category' | 'amount' | 'date' | 'bank' | undefined | null,
    sortDirection: 'ASC' | 'DESC',
    amountKeyword?: [number, number],
    descriptionKeyword?: string,
    categoryIds?: string[],
};

export type TransactionRow = {
    id: string;
    date: string;
    amount: number | string;
    balance: number | string | null;
    description: string;
    originalDescription?: string;
    bank: string;
    category: string;
    appliedTaxIds?: number[];
    type: 'real' | 'provision';
    provisionId?: number | null;
    staffPaymentId?: number | null;
    isRecurring?: boolean;
    isProvisioned?: boolean;
    invoiceNumber?: string | null;
    linkedTransactionId?: string | null;
    // Whether this row (real or provision) is on either side of a link -
    // a real transaction that's linked TO never has linkedTransactionId set
    // on itself, so this reverse-direction flag (computed server-side) is
    // what the "linked" icon actually needs to check.
    hasLinkedTransactions?: boolean;
    provisionThisTemplateId?: number | null;
    // The manual "OPEN"/"CLOSED" toggle - false means no more transactions/
    // provisions can be linked to this one. Null/undefined is treated as
    // closed, matching the detail modal's own fallback.
    isLinkable?: boolean;
    // Invoice was already PAID in Xero when imported - render like a real
    // transaction even though type stays 'provision' (see xero-import-service.js).
    // Already false whenever hasLinkedTransactions is true (see transactions-virtual.tsx).
    paidAtImport?: boolean;
    // Category/description were overwritten during CSV<->Xero reconciliation
    // (see xero-import-service.js) - original values are in the detail popup.
    reconciledWithXero?: boolean;
    // This row is linked to a Xero record at all (independent of whether
    // reconciledWithXero's description/category override happened - e.g.
    // inter-account transfers get xeroTransactionId without any override,
    // since they carry no AccountCode to resolve a category from).
    xeroTransactionId?: string | null;
    // Drives the "Moved" indicator, matching transactions-table-virtual.tsx's
    // own getProvisionDateShiftKind/originalDate handling.
    pushedForwardForAging?: boolean;
    dateAdjustedBack?: boolean;
    originalDate?: string | null;
    // Added by hand via "+ Transaction" - see TransactionRow.isManual in transactions-table-virtual.tsx.
    isManual?: boolean;
    // See TransactionRow.excludedFromBalance in transactions-table-virtual.tsx.
    excludedFromBalance?: boolean;
};

interface TransactionsTableMobileProps {
    data: TransactionRow[];
    onEdit?: (row: TransactionRow) => void;
    onDelete?: (row: TransactionRow, deleteScope?: 'this-occurrence' | 'this-and-future' | 'all-occurrences') => void;
    onTerminate?: (row: TransactionRow) => void;
    onLinkProvision?: (row: TransactionRow) => void;
    onProvisionThis?: (row: TransactionRow) => void;
    // "Move" in the provision row's action menu, matching the desktop
    // table's own action of the same name - opens the change-date popup.
    // Not offered for provisions already linked to a real transaction (money
    // already settled), same gate the desktop table applies.
    onChangeDate?: (row: TransactionRow) => void;
    // Bulk-select "real" rows via their checkbox, matching the desktop
    // table's own checkbox column - onMarkTransactions relates the checked
    // transactions to a provision picked from ProvisionSelectorPopup,
    // onProvisionThese builds a new provision template from their pattern
    // (only offered once 4+ are checked, same threshold the desktop uses).
    onMarkTransactions?: (transactionGuids: string[], provisionId?: number) => void;
    onProvisionThese?: (transactionGuids: string[]) => void;
    onTaxChange?: (transactionId: string, appliedTaxIds: number[]) => void;
    taxOptions?: { id: number; name: string; rate: number }[];
    bankOptions: DropdownOption[];
    categoryOptions: DropdownOption[];
    onRowClick?: (id: string, date: string, bank: string, category: string) => void;
    companyStatus?: {
        highestAmount?: number;
        lowestAmount?: number;
        latestTransaction?: Date | null;
        earliestTransaction?: Date | null;
        totalCount?: number;
    };
    stage?: 'initialize' | 'fetching' | 'complete';
    startDate?: Date;
    endDate?: Date;
    viewBy?: string;
    // New setters/values so the date column filter can control header-level filters
    setViewBy?: (value: string) => void;
    unitValue?: string;
    setUnitValue?: (value: string) => void;
    isDateRange?: boolean;
    setIsDateRange?: (value: boolean) => void;
    setStartDate?: (value: Date) => void;
    setEndDate?: (value: Date) => void;
    setColumnFilter?: React.Dispatch<React.SetStateAction<FilterParams | undefined>>;
    columnFilter?: FilterParams | undefined;
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
    // Renders a dense, table-style row (date/description/amount/cash
    // position only, no checkbox or action menu) instead of the detailed
    // card - an overview mode that trades detail for fitting more rows.
    compactView?: boolean;
}

// Mirrors transactions-table-virtual.tsx's own imperative handle - lets the
// page's mobile "Today" action (FAB menu) jump the list to today the same
// way the desktop table's Topbar button does, instead of that action only
// existing on desktop.
export type TransactionsTableMobileHandle = {
    scrollToToday: () => void;
};

// One entry per virtualized row - either a real/provision transaction row,
// one of the two range-boundary banners, or the trailing infinite-scroll
// spinner. Building this once (in the parent, via useMemo) lets react-window
// address every rendered thing - transaction or not - by a single flat index.
type ListEntry =
    | { kind: 'row'; row: TransactionRow; idx: number }
    | { kind: 'indicator'; variant: 'red' | 'orange'; label: string }
    | { kind: 'loading' };

type RowExtraProps = {
    items: ListEntry[];
    bankOptions: DropdownOption[];
    viewBy: string;
    formatDate: (date: string | Date) => string;
    formatAmount?: (n: number) => string;
    onRowClick?: (id: string, date: string, bank: string, category: string) => void;
    onEdit?: (row: TransactionRow) => void;
    onDelete?: (row: TransactionRow, deleteScope?: 'this-occurrence' | 'this-and-future' | 'all-occurrences') => void;
    onTerminate?: (row: TransactionRow) => void;
    onLinkProvision?: (row: TransactionRow) => void;
    onProvisionThis?: (row: TransactionRow) => void;
    // "Move" in the provision row's action menu, matching the desktop
    // table's own action of the same name - opens the change-date popup.
    // Not offered for provisions already linked to a real transaction (money
    // already settled), same gate the desktop table applies.
    onChangeDate?: (row: TransactionRow) => void;
    checkedRows: Set<string>;
    onCheckboxToggle: (rowId: string) => void;
    editDisabled: boolean;
    deleteDisabled: boolean;
    compactView: boolean;
};

// Same per-provision-subtype text color transactions-table-virtual.tsx uses
// for its own "Moved" icon - duplicated here for the same reason as
// getProvisionDateShiftKind below.
const getProvisionTextColor = (rowData: TransactionRow): string => {
    if (rowData.type !== 'provision' || rowData.paidAtImport) return 'inherit';
    if (rowData.invoiceNumber) return 'var(--color-invoice-provision-text)';
    if (rowData.staffPaymentId) return 'var(--color-payroll-provision-text)';
    if (rowData.isRecurring === true) return 'var(--color-recurring-provision-text)';
    return 'var(--color-onetime-provision-text)';
};

// Same "was this provision's date shifted away from where the recurrence/
// invoice originally put it" check transactions-table-virtual.tsx uses to
// drive its own "Moved" calendar icon - duplicated here rather than
// imported since the desktop table doesn't export it and the two files
// otherwise have no dependency on each other.
const getProvisionDateShiftKind = (rowData: TransactionRow): 'pushedForward' | 'pulledBack' | null => {
    if (rowData.type !== 'provision' || !!rowData.linkedTransactionId) return null;
    if (rowData.pushedForwardForAging === true) return 'pushedForward';
    if (rowData.dateAdjustedBack === true) return 'pulledBack';
    return null;
};

// Shared by both the detailed card and the compact table row - a row's date
// field is either a real date, a "YYYY" year-group label, or a "start to end"
// range-group label (month/quarter/week/year grouped views), each formatted
// differently per viewBy.
const formatRowDateLabel = (row: TransactionRow, viewBy: string, formatDate: (date: string | Date) => string): string => {
    if (typeof row.date === 'string' && /^\d{4}$/.test(row.date)) {
        return row.date;
    }
    if (typeof row.date === 'string' && row.date.includes(' to ')) {
        const [start, end] = row.date.split(' to ');
        const startDate = new Date(start);
        const endDate = new Date(end);
        const startFormatted = formatDate(startDate);
        const endFormatted = formatDate(endDate);
        if (viewBy === 'week') {
            const startParts = startFormatted.split(' ');
            const endParts = endFormatted.split(' ');
            return `${startParts[0]} ${startParts[1]}⇾${endParts[0]} ${endParts[1]} ${endParts[2]}`;
        }
        if (viewBy === 'month') {
            const endParts = endFormatted.split(' ');
            return `${endParts[1]} ${endParts[2]}`;
        }
        if (viewBy === 'quarter') {
            const quarterEndDate = new Date(end);
            const month = quarterEndDate.getMonth();
            const quarter = Math.floor(month / 3) + 1;
            const year = quarterEndDate.getFullYear();
            return `Q${quarter} ${year}`;
        }
        if (viewBy === 'day') {
            return endFormatted;
        }
        if (viewBy === 'year') {
            const endParts = endFormatted.split(' ');
            return endParts[2];
        }
        return `${startFormatted} to ${endFormatted}`;
    }
    return formatDate(new Date(row.date));
};

// Defined at module scope (not inside TransactionsTableMobile) so its
// identity never changes across renders - react-window remounts every row
// whenever the rowComponent reference changes, which a component declared
// inline in the parent's function body would do on every render.
const RangeIndicatorBanner: React.FC<{ variant: 'red' | 'orange'; label: string }> = ({ variant, label }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 30,
            width: '100%',
            background: variant === 'red' ? 'var(--color-end-of-horizon-bg)' : 'var(--color-filter-result-bg)',
            pl: 2,
            fontSize: 12,
            fontWeight: 500,
            color: variant === 'red' ? 'var(--color-end-of-horizon-text)' : 'var(--color-filter-result-text)',
            boxSizing: 'border-box',
        }}
    >
        {label}
    </Box>
);

const TransactionRowRenderer: React.FC<RowComponentProps<RowExtraProps>> = ({
    index,
    style,
    ariaAttributes,
    items,
    viewBy,
    formatDate,
    formatAmount,
    onRowClick,
    onEdit,
    onDelete,
    onTerminate,
    onLinkProvision,
    onProvisionThis,
    onChangeDate,
    checkedRows,
    onCheckboxToggle,
    editDisabled,
    deleteDisabled,
    compactView,
}) => {
    const item = items[index];
    if (!item) return <div style={style} {...ariaAttributes} />;

    if (item.kind === 'indicator') {
        return (
            <div style={{ ...style, paddingBottom: 4, boxSizing: 'border-box' }} {...ariaAttributes}>
                <RangeIndicatorBanner variant={item.variant} label={item.label} />
            </div>
        );
    }

    if (item.kind === 'loading') {
        return (
            <div style={{ ...style, paddingBottom: 4, boxSizing: 'border-box' }} {...ariaAttributes}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px 10px' }}>
                    <CircularProgress size={24} sx={{ color: 'var(--color-primary)' }} />
                </Box>
            </div>
        );
    }

    const { row, idx } = item;
    const isCurrentDayPlaceholder = row.id === 'current-day-placeholder';
    const isProvisioning = row.type === 'provision' && !row.paidAtImport;
    const amount = Number(row.amount);
    const amountColor = amount < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)';

    // Row-status badges (Linked/Related/Moved) - shown on the action button
    // itself (cycling through whichever apply, or static if just one) instead
    // of the plain ellipsis, and repeated at the top of its menu when opened.
    const rowStatusIcons: { icon: React.ReactNode; tooltip: string }[] = [];
    if (row.hasLinkedTransactions) {
        rowStatusIcons.push({
            icon: <Link size={16} color={isProvisioning ? 'var(--color-primary)' : '#1c1c1cff'} />,
            tooltip: row.type === 'provision' ? 'This provision is linked to a transaction' : 'This transaction is linked to a provision',
        });
    }
    if (row.type === 'real' && row.provisionThisTemplateId) {
        rowStatusIcons.push({
            icon: <ClipboardPaste size={16} color="#1c1c1cff" />,
            tooltip: 'This transaction is related to a provision via Provision This',
        });
    }
    const dateShiftKind = getProvisionDateShiftKind(row);
    if (dateShiftKind) {
        rowStatusIcons.push({
            icon: <CalendarClock size={16} color={getProvisionTextColor(row)} />,
            tooltip: row.originalDate
                ? `Originally scheduled for ${formatDate(new Date(row.originalDate))}`
                : dateShiftKind === 'pushedForward'
                    ? 'This provision was pushed forward by the aging job'
                    : "This provision's date was adjusted",
        });
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rowDate = new Date(row.date);
    rowDate.setHours(0, 0, 0, 0);
    const isCurrentDay = isCurrentDayPlaceholder || rowDate.getTime() === today.getTime();

    let paperBgColor = isProvisioning ? 'var(--color-provision-row-bg)' : 'var(--color-transaction-row-bg)';
    let borderColor = isProvisioning ? '#ffe5f0' : '#e0ecf7';

    if (isCurrentDay) {
        paperBgColor = 'var(--color-current-day-bg)';
        borderColor = '#ffb380';
    } else if (isProvisioning) {
        paperBgColor = idx % 2 === 0 ? 'var(--color-provision-row-bg)' : 'rgba(var(--color-provision-row-bg-rgb), 0.5)';
    } else {
        paperBgColor = idx % 2 === 0 ? 'var(--color-transaction-row-bg)' : 'rgba(var(--color-transaction-row-bg-rgb), 0.5)';
    }

    if (isCurrentDayPlaceholder) {
        return (
            <div style={{ ...style, paddingBottom: 4, boxSizing: 'border-box' }} {...ariaAttributes}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        background: paperBgColor,
                        border: `1px solid ${borderColor}`,
                        boxShadow: 'none',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 40,
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--color-default-text)' }}>
                        {formatDate(new Date(row.date))}
                    </Typography>
                </Paper>
            </div>
        );
    }

    if (compactView) {
        const dateColor = row.type === 'provision' ? 'var(--color-provision-text)' : 'var(--color-transaction-text)';
        return (
            <div style={{ ...style, boxSizing: 'border-box' }} {...ariaAttributes}>
                <Box
                    onClick={() => onRowClick?.(row.id, row.date, row.bank, row.category)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        px: 1,
                        minHeight: 28,
                        boxSizing: 'border-box',
                        background: paperBgColor,
                        borderBottom: `1px solid ${borderColor}`,
                        cursor: 'pointer',
                    }}
                >
                    <Typography sx={{ fontSize: 11, fontWeight: 600, width: 64, flexShrink: 0, color: dateColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {formatRowDateLabel(row, viewBy, formatDate)}
                    </Typography>
                    {viewBy === 'transactions' && (
                        <Typography sx={{ fontSize: 11, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isProvisioning ? 'var(--color-provision-text)' : 'var(--color-transaction-text)' }}>
                            {reformatGstDescriptionDates(row.description, formatDate)}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: 11, fontWeight: isProvisioning ? 700 : 600, width: 68, flexShrink: 0, textAlign: 'right', color: amountColor }}>
                        {formatAmount?.(amount)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, width: 76, flexShrink: 0, textAlign: 'right', color: row.balance !== null && !row.excludedFromBalance && Number(row.balance) < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)' }}>
                        {row.balance === null || row.balance === undefined || row.balance === '' || row.excludedFromBalance ? '-' : formatAmount?.(Number(row.balance))}
                    </Typography>
                </Box>
            </div>
        );
    }

    return (
        <div style={{ ...style, paddingBottom: 4, boxSizing: 'border-box' }} {...ariaAttributes}>
            <Paper
                elevation={0}
                sx={{
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    background: paperBgColor,
                    border: `1px solid ${borderColor}`,
                    boxShadow: 'none',
                    boxSizing: 'border-box',
                }}
            >
                {/* Header with collapsible toggle */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                    }}
                >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }} onClick={() => onRowClick?.(row.id, row.date, row.bank, row.category)}>
                        {/* Description now runs the full card width on its own
                            line instead of sharing row space with Amount/Cash
                            Pos - those columns lost their per-card labels once
                            the list gained a sticky column header, which freed
                            up the width description used to be squeezed into. */}
                        { viewBy === 'transactions' &&
                            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 500, color: '#707070', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                {row.reconciledWithXero && <RefreshCw size={11} style={{ flexShrink: 0 }} />}
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {reformatGstDescriptionDates(row.description, formatDate)}
                                </Box>
                            </Typography>
                        }
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1, color: row.type === 'provision' ? 'var(--color-provision-text)' : 'var(--color-transaction-text)' }}>
                                {formatRowDateLabel(row, viewBy, formatDate)}
                            </Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: isProvisioning ? 700 : 600, flex: 0.85, color: amountColor }}>
                                {formatAmount?.(amount)}
                            </Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 0.85, color: row.balance !== null && !row.excludedFromBalance && Number(row.balance) < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)' }}>
                                {row.balance === null || row.balance === undefined || row.balance === '' || row.excludedFromBalance ? '-' : formatAmount?.(Number(row.balance))}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexDirection: 'column', justifyContent: 'center' }}>
                        {/* Checkbox on its own row above the actions, same
                            1-column/2-row layout used for the corner controls
                            on the Provisions page's mobile card. */}
                        {row.type === 'real' && (
                            <Checkbox
                                checked={checkedRows.has(row.id)}
                                onChange={() => onCheckboxToggle(row.id)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ p: 0, width: 20, height: 20 }}
                            />
                        )}
                        <Box onClick={(e) => e.stopPropagation()}>
                                {row.type === 'real' && (
                                    <ActionMenu
                                        actions={[
                                            {
                                                label: 'Link a Provision',
                                                onClick: () => onLinkProvision && onLinkProvision(row),
                                                icon: 'link',
                                                disabled: editDisabled || row.isLinkable === false || !!row.provisionThisTemplateId,
                                                tooltip: row.provisionThisTemplateId
                                                    ? 'This transaction is already tracked by a provision via Provision This.'
                                                    : row.isLinkable === false ? 'This transaction is closed and cannot accept more links.' : undefined,
                                            },
                                            ...(!row.provisionThisTemplateId ? [{
                                                label: 'Provision This',
                                                onClick: () => onProvisionThis && onProvisionThis(row),
                                                icon: 'paste' as const,
                                                disabled: editDisabled || !!row.hasLinkedTransactions,
                                                tooltip: row.hasLinkedTransactions ? 'This transaction is linked to a provision.' : undefined,
                                            }] : []),
                                            // Only for transactions added via "+ Transaction" (never
                                            // imported ones); blocked while linked - see the matching
                                            // comment in transactions-table-virtual.tsx.
                                            ...(row.isManual ? [
                                                {
                                                    label: 'Edit',
                                                    onClick: () => onEdit && onEdit(row),
                                                    icon: 'edit' as const,
                                                    disabled: editDisabled || !!row.hasLinkedTransactions || !!row.provisionThisTemplateId,
                                                    tooltip: (row.hasLinkedTransactions || row.provisionThisTemplateId) ? 'Unlink this transaction from its provision before editing it.' : undefined,
                                                },
                                                {
                                                    label: 'Delete',
                                                    onClick: () => onDelete && onDelete(row),
                                                    icon: 'delete' as const,
                                                    disabled: deleteDisabled || !!row.hasLinkedTransactions || !!row.provisionThisTemplateId,
                                                    tooltip: (row.hasLinkedTransactions || row.provisionThisTemplateId) ? 'Unlink this transaction from its provision before deleting it.' : undefined,
                                                },
                                            ] : []),
                                        ]}
                                        statusIcons={rowStatusIcons}
                                    />
                                )}
                                {row.type === 'provision' && (
                                    <ActionMenu
                                        statusIcons={rowStatusIcons}
                                        actions={[
                                            { label: 'Edit', onClick: () => onEdit && onEdit(row), icon: 'edit', disabled: editDisabled },
                                            {
                                                label: 'Link a Transaction',
                                                onClick: () => onLinkProvision && onLinkProvision(row),
                                                icon: 'link',
                                                disabled: editDisabled || row.isLinkable === false,
                                                tooltip: row.isLinkable === false ? 'This provision is closed and cannot accept more links.' : undefined,
                                            },
                                            // Linked provisions represent money already settled against a
                                            // real transaction - changing their date doesn't make sense
                                            // (matches the server-side guard in updateProvisionDate).
                                            ...(!row.linkedTransactionId ? [{ label: 'Move', onClick: () => onChangeDate && onChangeDate(row), icon: 'calendar' as const, disabled: editDisabled }] : []),
                                            row.isRecurring ? {
                                                label: 'Delete',
                                                icon: 'delete' as const,
                                                disabled: deleteDisabled,
                                                submenu: [
                                                    { label: 'Delete this occurrence only', onClick: () => onDelete && onDelete(row, 'this-occurrence'), icon: 'delete' as const, disabled: deleteDisabled },
                                                    { label: 'Delete this and future occurrences', onClick: () => onDelete && onDelete(row, 'this-and-future'), icon: 'delete' as const, disabled: deleteDisabled },
                                                    { label: 'Delete all occurrences', onClick: () => onDelete && onDelete(row, 'all-occurrences'), icon: 'delete' as const, disabled: deleteDisabled },
                                                    { label: 'Terminate', onClick: () => onTerminate && onTerminate(row), icon: 'warning' as const, disabled: editDisabled },
                                                ],
                                            } : { label: 'Delete', onClick: () => onDelete && onDelete(row), icon: 'delete' as const, disabled: deleteDisabled }
                                        ]}
                                    />
                                )}
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </div>
    );
};

const TransactionsTableMobile = forwardRef<TransactionsTableMobileHandle, TransactionsTableMobileProps>(({
    data,
    onEdit,
    onDelete,
    onTerminate,
    onLinkProvision,
    onProvisionThis,
    onChangeDate,
    onMarkTransactions,
    onProvisionThese,
    bankOptions,
    onRowClick,
    companyStatus,
    stage = 'fetching',
    viewBy = 'transactions',
    setViewBy,
    unitValue,
    setUnitValue,
    isDateRange,
    setIsDateRange,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setColumnFilter,
    columnFilter,
    hasNextPage,
    onLoadMore,
    isLoadingMore,
    canUpdate,
    canDelete,
    compactView = false,
}, ref) => {
    const { formatDate, formatAmount } = useSettings();
    // Server-verified per-entity admin flag - see table-virtual.tsx for why
    // User.role (login cookie) isn't used here.
    const { isAdmin } = usePermissions();
    // Admins always pass; anyone else needs the permission the page grants
    // (this used to also require being an admin).
    const editDisabled = !isAdmin && !canUpdate;
    const deleteDisabled = !isAdmin && !canDelete;
    const listRef = useListRef(null);
    const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set());
    const [showProvisionSelector, setShowProvisionSelector] = useState(false);
    const [filterConfig, setFilterConfig] = React.useState<FilterParams>({
        sortBy: null,
        sortDirection: 'DESC'
    });

    // Rows can grow/shrink entering/leaving the "transactions" view (which
    // shows a description line) - the dynamic cache remeasures automatically
    // via ResizeObserver whenever a row is actually rendered, but resetting
    // on viewBy/compactView keeps a clean slate instead of momentarily
    // reusing wildly-wrong heights from a completely different row layout
    // (e.g. month/quarter view rows are shorter, and compact rows shorter still).
    const dynamicRowHeight = useDynamicRowHeight({ defaultRowHeight: compactView ? 30 : 90, key: `${viewBy}-${compactView}` });

    const handleCheckboxToggle = useCallback((rowId: string) => {
        setCheckedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    }, []);

    useEffect(() => {
        if (columnFilter) {
            setFilterConfig(columnFilter);
        }
    }, [columnFilter]);

    // Derive the boundary banners (top/bottom "start of records"/"end of
    // horizon"/filter-result labels) and, when viewing the plain
    // transactions list sorted by date, splice in a blank placeholder row
    // for "today" if it isn't already present - same logic the row list
    // used to compute inline, lifted out so it can feed the virtualized
    // items array below.
    const { processedData, topIndicatorType, topLabel, bottomIndicatorType, bottomLabel } = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const hasCurrentDay = data.some(row => {
            const rowDate = new Date(row.date);
            rowDate.setHours(0, 0, 0, 0);
            return rowDate.getTime() === today.getTime();
        });

        let processed = [...data];

        if (!hasCurrentDay && viewBy === 'transactions' && (filterConfig.sortBy === null || filterConfig.sortBy === 'date')) {
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            const todayStr = `${year}-${month}-${day}`;

            const blankRow: TransactionRow = {
                id: 'current-day-placeholder',
                date: todayStr,
                amount: '',
                balance: '',
                description: '',
                bank: '',
                category: '',
                type: 'real'
            };

            if (data.length === 0) {
                processed.push(blankRow);
            } else {
                const firstDate = new Date(data[0].date);
                firstDate.setHours(0, 0, 0, 0);
                const secondDate = data.length > 1 ? new Date(data[1].date) : null;
                if (secondDate) secondDate.setHours(0, 0, 0, 0);

                const isDescending = secondDate ? firstDate.getTime() >= secondDate.getTime() : false;

                let inserted = false;
                if (isDescending) {
                    for (let i = 0; i < processed.length; i++) {
                        const rowDate = new Date(processed[i].date);
                        rowDate.setHours(0, 0, 0, 0);
                        if (rowDate.getTime() < today.getTime()) {
                            processed.splice(i, 0, blankRow);
                            inserted = true;
                            break;
                        }
                    }
                } else {
                    for (let i = 0; i < processed.length; i++) {
                        const rowDate = new Date(processed[i].date);
                        rowDate.setHours(0, 0, 0, 0);
                        if (rowDate.getTime() > today.getTime()) {
                            processed.splice(i, 0, blankRow);
                            inserted = true;
                            break;
                        }
                    }
                }

                if (!inserted) {
                    processed.push(blankRow);
                }
            }
        }

        const isSortAsc = filterConfig?.sortDirection === 'ASC';

        const firstRecord = processed[0];
        const isFirstRecordEarliest = firstRecord && companyStatus?.earliestTransaction &&
            new Date(firstRecord.date).toDateString() === new Date(companyStatus.earliestTransaction).toDateString();
        const isFirstRecordLatest = firstRecord && companyStatus?.latestTransaction &&
            new Date(firstRecord.date).toDateString() === new Date(companyStatus.latestTransaction).toDateString();

        const lastRecord = processed[processed.length - 1];
        const isLastRecordLatest = lastRecord && companyStatus?.latestTransaction &&
            new Date(lastRecord.date).toDateString() === new Date(companyStatus.latestTransaction).toDateString();
        const isLastRecordEarliest = lastRecord && companyStatus?.earliestTransaction &&
            new Date(lastRecord.date).toDateString() === new Date(companyStatus.earliestTransaction).toDateString();

        let topType: 'red' | 'orange' = 'red';
        let topLbl = 'Start of records';
        if (isSortAsc) {
            if (isFirstRecordEarliest) {
                topType = 'red';
                topLbl = 'Start of Records';
            } else {
                topType = 'orange';
                topLbl = 'Beginning of filter results';
            }
        } else {
            if (isFirstRecordLatest) {
                topType = 'red';
                topLbl = 'End of Horizon';
            } else {
                topType = 'orange';
                topLbl = 'Beginning of filter results';
            }
        }

        let bottomType: 'red' | 'orange' = 'red';
        let bottomLbl = 'End of Horizon';
        if (isSortAsc) {
            if (isLastRecordLatest) {
                bottomType = 'red';
                bottomLbl = 'End of Horizon';
            } else {
                bottomType = 'orange';
                bottomLbl = 'End of filter results';
            }
        } else {
            if (isLastRecordEarliest) {
                bottomType = 'red';
                bottomLbl = 'Start of Records';
            } else {
                bottomType = 'orange';
                bottomLbl = 'End of filter results';
            }
        }

        return { processedData: processed, topIndicatorType: topType, topLabel: topLbl, bottomIndicatorType: bottomType, bottomLabel: bottomLbl };
    }, [data, viewBy, filterConfig.sortBy, filterConfig.sortDirection, companyStatus?.earliestTransaction, companyStatus?.latestTransaction]);

    // Flat, index-addressable list react-window renders from - the two
    // boundary banners plus every row, plus a trailing spinner while more
    // are being fetched.
    const items: ListEntry[] = useMemo(() => {
        const list: ListEntry[] = [];
        if (processedData.length > 0) {
            list.push({ kind: 'indicator', variant: topIndicatorType, label: topLabel });
        }
        processedData.forEach((row, idx) => list.push({ kind: 'row', row, idx }));
        if (processedData.length > 0) {
            list.push({ kind: 'indicator', variant: bottomIndicatorType, label: bottomLabel });
        }
        if (isLoadingMore) {
            list.push({ kind: 'loading' });
        }
        return list;
    }, [processedData, topIndicatorType, topLabel, bottomIndicatorType, bottomLabel, isLoadingMore]);

    const currentDayIndex = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return items.findIndex(item => {
            if (item.kind !== 'row') return false;
            if (item.row.id === 'current-day-placeholder') return true;
            const rowDate = new Date(item.row.date);
            rowDate.setHours(0, 0, 0, 0);
            return rowDate.getTime() === today.getTime();
        });
    }, [items]);

    useImperativeHandle(ref, () => ({
        scrollToToday: () => {
            if (currentDayIndex >= 0) {
                listRef.current?.scrollToRow({ index: currentDayIndex, align: 'center', behavior: 'auto' });
            }
        },
    }), [currentDayIndex]);

    // Auto-scroll to today after loading or when filters change (mirrors
    // the desktop virtualized table's scrollToToday behavior).
    useEffect(() => {
        if (stage === 'complete' && currentDayIndex >= 0) {
            const timer = setTimeout(() => {
                listRef.current?.scrollToRow({ index: currentDayIndex, align: 'center', behavior: 'auto' });
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [stage, columnFilter, currentDayIndex]);

    const handleRowsRendered = useCallback((visible: { startIndex: number; stopIndex: number }) => {
        if (hasNextPage && !isLoadingMore && onLoadMore && visible.stopIndex >= items.length - 5) {
            onLoadMore();
        }
    }, [hasNextPage, isLoadingMore, onLoadMore, items.length]);

    const rowProps: RowExtraProps = useMemo(() => ({
        items,
        bankOptions,
        viewBy,
        formatDate,
        formatAmount,
        onRowClick,
        onEdit,
        onDelete,
        onTerminate,
        onLinkProvision,
        onProvisionThis,
        onChangeDate,
        checkedRows,
        onCheckboxToggle: handleCheckboxToggle,
        editDisabled,
        deleteDisabled,
        compactView,
    }), [items, bankOptions, viewBy, formatDate, formatAmount, onRowClick, onEdit, onDelete, onTerminate, onLinkProvision, onProvisionThis, onChangeDate, checkedRows, handleCheckboxToggle, editDisabled, deleteDisabled, compactView]);

    return (
        <Box sx={{ width: '100%', p: 0, boxSizing: 'border-box' }}>
            {stage !== 'complete' ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }} fontSize={16}>
                        {stage === 'initialize' ? 'Initializing filters...' : 'Loading Transactions...'}
                    </Typography>
                </Box>
            ) : (!data || (Array.isArray(data) && data.length === 0)) && stage === 'complete' ? (
                <TablePlaceholder
                    title="No Transactions Found"
                    message={(!companyStatus?.totalCount || companyStatus?.totalCount === 0) && !companyStatus?.earliestTransaction
                        ? "Get started by importing transactions or creating categories."
                        : `You have ${companyStatus?.totalCount || 0} transactions and provisions from ${companyStatus?.earliestTransaction ? formatDate(companyStatus.earliestTransaction) : 'N/A'} to ${companyStatus?.latestTransaction ? formatDate(companyStatus.latestTransaction) : 'N/A'}. Adjust your filters to view results.`}
                    latestTransaction={companyStatus?.latestTransaction}
                    earliestTransaction={companyStatus?.earliestTransaction}
                    totalCount={companyStatus?.totalCount}
                />
            ) : (
                <>
                {checkedRows.size > 0 && (
                    <Box sx={{
                        position: 'fixed',
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 999,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                    }}>
                        <Button
                            variant="contained"
                            disabled={editDisabled}
                            onClick={() => setShowProvisionSelector(true)}
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                px: 2.5,
                                py: 1,
                                background: 'var(--color-primary)',
                                color: '#fff',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                                '&:hover': { background: 'var(--color-primary)' },
                            }}
                        >
                            Relate to Provision ({checkedRows.size})
                        </Button>
                        {checkedRows.size >= 4 && (
                            <Button
                                variant="outlined"
                                disabled={editDisabled}
                                onClick={() => {
                                    onProvisionThese?.(Array.from(checkedRows));
                                    setCheckedRows(new Set());
                                }}
                                sx={{
                                    borderRadius: 999,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    px: 2.5,
                                    py: 1,
                                    background: '#fff',
                                    borderColor: 'var(--color-primary)',
                                    borderWidth: 2,
                                    color: 'var(--color-primary)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                                    '&:hover': { background: '#fff', borderColor: 'var(--color-primary)', borderWidth: 2 },
                                }}
                            >
                                Provision These ({checkedRows.size})
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={() => setCheckedRows(new Set())}
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                px: 2.5,
                                py: 1,
                                background: '#fff',
                                color: 'var(--color-text-muted)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                                '&:hover': { background: '#fff' },
                            }}
                        >
                            Clear
                        </Button>
                    </Box>
                )}
                {compactView ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            px: 1,
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            background: 'var(--color-page-background)',
                            borderBottom: '1px solid #e0e0e0',
                        }}
                    >
                        <Typography sx={{ fontSize: 10, fontWeight: 700, width: 64, flexShrink: 0, color: '#707070', textTransform: 'uppercase' }}>
                            Date
                        </Typography>
                        {viewBy === 'transactions' && (
                            <Typography sx={{ fontSize: 10, fontWeight: 700, flex: 1, minWidth: 0, color: '#707070', textTransform: 'uppercase' }}>
                                Description
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: 10, fontWeight: 700, width: 68, flexShrink: 0, textAlign: 'right', color: '#707070', textTransform: 'uppercase' }}>
                            Amount
                        </Typography>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, width: 76, flexShrink: 0, textAlign: 'right', color: '#707070', textTransform: 'uppercase' }}>
                            Cash Pos
                        </Typography>
                    </Box>
                ) : (
                    // Labels Date/Amount/Cash Pos once here instead of on every
                    // card, now that those columns no longer carry their own
                    // "Amount"/"Cash Pos" text (see the row renderer) - the
                    // trailing spacer accounts for the corner checkbox/action
                    // column so these line up with the values under them.
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            py: 0.5,
                            px: 1,
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            background: 'var(--color-page-background)',
                            borderBottom: '1px solid #e0e0e0',
                        }}
                    >
                        <Typography sx={{ fontSize: 10, fontWeight: 700, flex: 1, minWidth: 0, color: '#707070', textTransform: 'uppercase' }}>
                            Date
                        </Typography>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, flex: 0.85, color: '#707070', textTransform: 'uppercase' }}>
                            Amount
                        </Typography>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, flex: 0.85, color: '#707070', textTransform: 'uppercase' }}>
                            Cash Pos
                        </Typography>
                        <Box sx={{ width: 36, flexShrink: 0 }} />
                    </Box>
                )}
                <List
                    listRef={listRef}
                    rowComponent={TransactionRowRenderer}
                    rowCount={items.length}
                    rowHeight={dynamicRowHeight}
                    rowProps={rowProps}
                    onRowsRendered={handleRowsRendered}
                    overscanCount={8}
                    style={{ height: 'calc(100vh - 168px)', width: '100%' }}
                />
                </>
            )}
            <ProvisionSelectorPopup
                open={showProvisionSelector}
                onClose={() => setShowProvisionSelector(false)}
                onSelect={(provisionId) => {
                    const selectedGuids = Array.from(checkedRows);
                    onMarkTransactions?.(selectedGuids, provisionId);
                    setCheckedRows(new Set());
                }}
            />
        </Box>
    );
});

TransactionsTableMobile.displayName = 'TransactionsTableMobile';

export default TransactionsTableMobile;
